import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { BACKEND_URL } from '../context/AuthContext';
import {
  Loader2, AlertTriangle, ShieldCheck, TrendingUp, ArrowRight,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';

function HealthGauge({ score, level, color }) {
  const gaugeColor = color === 'green' ? '#10b981' : color === 'amber' ? '#f59e0b' : color === 'orange' ? '#f97316' : '#ef4444';
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="flex flex-col items-center" data-testid="share-health-gauge">
      <svg width="160" height="160" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="54" fill="none" stroke="#1e293b" strokeWidth="8" />
        <circle cx="60" cy="60" r="54" fill="none" stroke={gaugeColor} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 60 60)"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
        <text x="60" y="52" textAnchor="middle" fill={gaugeColor} style={{ fontSize: '28px', fontFamily: 'monospace', fontWeight: 'bold' }}>{score}</text>
        <text x="60" y="70" textAnchor="middle" fill="#94a3b8" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{level}</text>
      </svg>
    </div>
  );
}

export default function ShareReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/report/${token}`);
        if (res.status === 410) { setError('expired'); return; }
        if (!res.ok) { setError('not_found'); return; }
        const data = await res.json();
        setReport(data.report);
        setExpiresAt(data.expires_at);
      } catch {
        setError('network');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [token]);

  const handleCtaClick = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/public/report/${token}/cta-click`, { method: 'POST' });
    } catch { /* best effort */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    const msgs = {
      expired: 'This share link has expired.',
      not_found: 'Report not found.',
      network: 'Unable to load report.',
    };
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4" data-testid="share-error">
        <AlertTriangle className="w-8 h-8 text-amber-400" />
        <p className="text-slate-300">{msgs[error]}</p>
        <Link to="/beta">
          <Button variant="outline" className="border-orange-500/30 text-orange-400">
            Try BidSentry
          </Button>
        </Link>
      </div>
    );
  }

  const { bid_health, margin_analysis, risk_assessment, sensitivity, competitive_position, executive_commentary, underbid_flags, bid_ref, client_name, project_type } = report;

  const sensitivityData = sensitivity?.variations?.filter(v => v.type === 'materials').map(v => ({
    name: `${v.variation_pct > 0 ? '+' : ''}${v.variation_pct}%`,
    margin: v.margin_pct,
  })) || [];

  return (
    <div className="min-h-screen bg-slate-950" data-testid="share-report-page">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-orange-500" />
            <span className="font-black text-sm text-white tracking-widest" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
            <Badge variant="outline" className="text-[9px] text-slate-500 border-slate-700 ml-2">SHARED REPORT</Badge>
          </div>
          {expiresAt && (
            <span className="text-[10px] text-slate-600 font-mono">
              Expires {new Date(expiresAt).toLocaleDateString('en-GB')}
            </span>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Bid info + health score */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }} data-testid="share-bid-ref">{bid_ref}</h1>
            <p className="text-slate-400 text-sm">{client_name}</p>
            {project_type && <Badge variant="outline" className="mt-2 text-[10px] text-slate-500 border-slate-700">{project_type}</Badge>}
          </div>
          {bid_health && (
            <div className="flex flex-col items-center gap-1">
              <HealthGauge score={bid_health.score} level={bid_health.level} color={bid_health.color} />
              {bid_health.risk_classification && (
                <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm ${
                  bid_health.color === 'green' ? 'bg-green-500/10 text-green-400' :
                  bid_health.color === 'amber' ? 'bg-amber-500/10 text-amber-400' :
                  bid_health.color === 'orange' ? 'bg-orange-500/10 text-orange-400' :
                  'bg-red-500/10 text-red-400'
                }`} data-testid="share-risk-classification">
                  {bid_health.risk_classification}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="pt-5 pb-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Gross Margin</p>
              <p className={`text-xl font-mono font-bold ${
                margin_analysis?.gross_margin_pct >= 15 ? 'text-green-400' :
                margin_analysis?.gross_margin_pct >= 0 ? 'text-orange-400' : 'text-red-400'
              }`} data-testid="share-margin">
                {margin_analysis?.gross_margin_pct?.toFixed(1)}%
              </p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="pt-5 pb-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Risk Spread</p>
              <p className="text-xl font-mono font-bold text-slate-100" data-testid="share-risk-spread">
                ±{sensitivity?.risk_spread?.toFixed(1)}%
              </p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="pt-5 pb-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Profitable</p>
              <p className={`text-xl font-mono font-bold ${margin_analysis?.is_profitable ? 'text-green-400' : 'text-red-400'}`}>
                {margin_analysis?.is_profitable ? 'Yes' : 'No'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Competitive Position */}
        {competitive_position && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">Competitive Position</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative h-8 bg-slate-800 rounded-sm overflow-hidden">
                <div className="absolute inset-0 flex">
                  <div className="w-1/3 border-r border-slate-700 bg-red-500/10" />
                  <div className="w-1/3 border-r border-slate-700 bg-green-500/10" />
                  <div className="w-1/3 bg-amber-500/10" />
                </div>
                <div className="absolute top-0 bottom-0 w-1 bg-orange-500 rounded"
                  style={{ left: `${competitive_position.position_percent || 50}%` }} />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[9px] text-red-400">Undercut</span>
                <span className="text-[9px] text-green-400">Sweet Spot</span>
                <span className="text-[9px] text-amber-400">Premium</span>
              </div>
              {competitive_position.deviation_text && (
                <p className="text-[10px] text-slate-500 mt-2 font-mono">{competitive_position.deviation_text}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Executive Commentary */}
        {executive_commentary?.paragraphs && (
          <Card className="bg-slate-900 border-slate-800" data-testid="share-executive-commentary">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">Executive Commentary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {executive_commentary.paragraphs.map((p, i) => (
                  <p key={i} className="text-sm text-slate-300 leading-relaxed font-mono tracking-tight">{p}</p>
                ))}
              </div>
              {executive_commentary.recommendation && (
                <div className="mt-4 pt-4 border-t border-slate-800">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Recommendation</p>
                  <p className="text-sm text-slate-200 font-medium">{executive_commentary.recommendation}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Underbid Flags */}
        {underbid_flags?.length > 0 && (
          <Card className="bg-slate-900 border-amber-500/20" data-testid="share-warnings">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5" /> Warnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5">
                {underbid_flags.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <span className={`inline-block w-1.5 h-1.5 mt-2 rounded-full flex-shrink-0 ${
                      f.severity === 'critical' ? 'bg-red-400' : f.severity === 'high' ? 'bg-orange-400' : 'bg-amber-400'
                    }`} />
                    {f.message}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Sensitivity Chart */}
        {sensitivityData.length > 0 && (
          <Card className="bg-slate-900 border-slate-800" data-testid="share-sensitivity">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">Sensitivity (Materials)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sensitivityData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#64748b' }} domain={['dataMin - 5', 'dataMax + 5']} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '2px', color: '#fff' }}
                      formatter={(v) => [`${v}%`, 'Margin']} />
                    <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
                    <Bar dataKey="margin" radius={[2, 2, 0, 0]}>
                      {sensitivityData.map((entry, i) => (
                        <Cell key={i} fill={entry.margin >= 15 ? '#10b981' : entry.margin >= 0 ? '#f97316' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk Breakdown */}
        {risk_assessment?.assessments?.length > 0 && (
          <Card className="bg-slate-900 border-slate-800" data-testid="share-risk-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">Risk Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3">
                {risk_assessment.assessments.map((a, i) => (
                  <div key={i} className="bg-slate-800/40 rounded-sm p-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs text-slate-300 capitalize">{a.module_name?.replace(/_/g, ' ')}</span>
                      <span className="text-xs font-mono text-slate-400">{a.score?.toFixed(0)}/100</span>
                    </div>
                    <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full ${a.score <= 30 ? 'bg-green-500' : a.score <= 60 ? 'bg-orange-500' : 'bg-red-500'}`}
                        style={{ width: `${a.score}%` }} />
                    </div>
                    <ul className="mt-1.5 space-y-0.5">
                      {a.findings?.map((f, j) => (
                        <li key={j} className="text-[10px] text-slate-500">{f}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer — CTA */}
      <footer className="border-t border-slate-800 bg-slate-900/50 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center space-y-4" data-testid="share-footer">
          <p className="text-[10px] text-slate-600 uppercase tracking-[0.2em]">Generated with BidSentry</p>
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            Want to analyse your own bids?
          </h2>
          <p className="text-sm text-slate-400 max-w-md mx-auto">
            BidSentry analyses tender prices before submission and flags when a bid may be undercutting safe margin levels.
          </p>
          <Link to="/beta?ref=share" onClick={handleCtaClick}>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white mt-2" data-testid="share-cta-btn">
              <TrendingUp className="w-4 h-4 mr-2" />
              Run your own Bid Health analysis
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </footer>
      <div className="bg-slate-950 py-3 text-center" data-testid="share-ai-disclosure">
        <p className="text-[10px] text-slate-600">
          Generated using deterministic commercial modelling and AI-assisted commentary. Not professional advice.
        </p>
      </div>
    </div>
  );
}
